import { getAPIURL } from "./config";

const handleError = (response, errorText, defaultMsg) => {
  let message = defaultMsg;
  if (response.status === 422) {
    try {
      const err = JSON.parse(errorText);
      const details = Array.isArray(err.detail)
        ? err.detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : err.detail;
      if (details) message = `Ошибка валидации: ${details}`;
    } catch {
      if (errorText) message += `. ${errorText}`;
    }
  } else if (errorText) {
    message += `. ${errorText}`;
  }
  throw new Error(message);
};

/**
 * Список пользователей с отчётами по ЭП за месяц
 * GET /sketch_design/payments_progress/users?month=&year=
 */
export const getPaymentsProgressUsers = async (month, year) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/payments_progress/users?month=${month}&year=${year}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки списка пользователей: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

/**
 * Проекты, по которым работал пользователь за месяц
 * GET /sketch_design/payments_progress/users/projects?month=&year=&user_id=
 */
export const getPaymentsProgressUserProjects = async (month, year, userId) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/payments_progress/users/projects?month=${month}&year=${year}&user_id=${userId}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки проектов: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

/**
 * Список проектов с отчётами по ЭП за месяц
 * GET /sketch_design/payments_progress/projects?month=&year=
 */
export const getPaymentsProgressProjects = async (month, year) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/payments_progress/projects?month=${month}&year=${year}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки списка проектов: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

/**
 * Пользователи, работавшие по проекту за месяц
 * GET /sketch_design/payments_progress/projects/users?month=&year=&project_id=
 */
export const getPaymentsProgressProjectUsers = async (
  month,
  year,
  projectId,
) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/payments_progress/projects/users?month=${month}&year=${year}&project_id=${projectId}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки пользователей проекта: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

/**
 * Статистика по видам работ пользователя по проекту (с корректировками)
 * GET /sketch_design/payments_progress/get?month=&year=&user_id=&project_id=
 */
export const getPaymentsProgressGet = async (
  month,
  year,
  userId,
  projectId,
) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/payments_progress/get?month=${month}&year=${year}&user_id=${userId}&project_id=${projectId}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки статистики: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

/**
 * Обновить корректировку
 * PATCH /sketch_design/payments_progress/correction/update
 * @param {{ id: number, month: number, year: number, completion_percentage_after_correction?: number, completion_sum_after_correction?: number, comment?: string }} body
 */
export const updatePaymentsProgressCorrection = async (body) => {
  const normalizedId = Number(body?.id ?? body?.correction_id);
  const payload = {
    ...body,
    id: normalizedId,
    correction_id: normalizedId,
  };

  const response = await fetch(
    `${getAPIURL()}sketch_design/payments_progress/correction/update`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка обновления корректировки: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

/**
 * Создать согласование по Прогрессу выплат
 * POST /sketch_design/approvals/payments/create?year=&month=&user_id=
 * Body: number[] (id согласующих)
 */
export const createPaymentsProgressApproval = async (
  year,
  month,
  userId,
  approverIds,
) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/payments/create?year=${year}&month=${month}&user_id=${userId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify(approverIds),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка отправки на согласование: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Получить согласование по Прогрессу выплат
 * GET /sketch_design/approvals/payments/get?year=&month=&user_id=
 */
export const getPaymentsProgressApproval = async (year, month, userId) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/payments/get?year=${year}&month=${month}&user_id=${userId}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );

  if (response.status === 404 || response.status === 422) {
    return null;
  }

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки согласования выплат: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Получить все согласования выплат, где текущий пользователь является согласующим
 * GET /sketch_design/approvals/payments/approvals_by_approver
 */
export const getPaymentsProgressApprovalsByApprover = async () => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/payments/approvals_by_approver`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки согласований выплат по согласующему: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Обновить статус согласующего по Прогрессу выплат
 * PATCH /sketch_design/approvals/payments/update/{payments_approver_id}/{status}
 * @param {number} paymentsApproverId - ID записи согласующего (approver.id)
 * @param {'approved'|'in_processing'|'rejected'} status
 */
export const updatePaymentsProgressApproverStatus = async (
  paymentsApproverId,
  status,
) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/payments/update/${paymentsApproverId}/${status}`,
    {
      method: "PATCH",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка обновления статуса согласования выплат: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

/**
 * Удалить согласование по Прогрессу выплат
 * DELETE /sketch_design/approvals/payments/delete/{payments_approval_id}
 */
export const deletePaymentsProgressApproval = async (paymentsApprovalId) => {
  const response = await fetch(
    `${getAPIURL()}sketch_design/approvals/payments/delete/${paymentsApprovalId}`,
    {
      method: "DELETE",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка удаления согласования выплат: ${response.status} ${response.statusText}`,
    );
  }

  return response.text();
};

/**
 * Получить все проекты Генплан РП
 * GET /general_design/all?month=&year=
 */
export const getGeneralDesignPaymentsProgress = async (month, year) => {
  const response = await fetch(
    `${getAPIURL()}general_design/all?month=${month}&year=${year}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки Генплан РП: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Получить проекты Генплан РП, по которым есть отчеты
 * GET /general_design/projects-with-reports?month=&year=
 */
export const getGeneralDesignAllContracts = async (month, year) => {
  const response = await fetch(
    `${getAPIURL()}general_design/projects-with-reports?month=${month}&year=${year}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки списка проектов Генплан РП: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Получить пользователей с отчетами по разделу договора Генплан РП
 * GET /general_design/users_with_reports?month=&year=&contract_sections_id=
 */
export const getGeneralDesignUsersWithReports = async (
  month,
  year,
  contractSectionsId,
) => {
  const response = await fetch(
    `${getAPIURL()}general_design/users_with_reports?month=${month}&year=${year}&contract_sections_id=${contractSectionsId}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки пользователей Генплан РП: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Получить отчеты пользователя по разделу договора Генплан РП
 * GET /general_design/user_reports?month=&year=&contract_sections_id=&user_id=
 */
export const getGeneralDesignUserReports = async (
  month,
  year,
  contractSectionsId,
  userId,
) => {
  const response = await fetch(
    `${getAPIURL()}general_design/user_reports?month=${month}&year=${year}&contract_sections_id=${contractSectionsId}&user_id=${userId}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки отчетов пользователя Генплан РП: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Обновить корректировку Генплан РП
 * PATCH /general_design/correction
 * @param {{ contract_sections_id: number, user_id: number, month: number, year: number, completion_percentage_after_correction?: number, completion_sum_after_correction?: number, comment?: string }} body
 */
export const updateGeneralDesignCorrection = async (body) => {
  const {
    contract_sections_id,
    user_id,
    month,
    year,
    completion_percentage_after_correction,
    completion_sum_after_correction,
    comment,
  } = body || {};

  const query = new URLSearchParams({
    user_id: String(user_id),
    month: String(month),
    year: String(year),
    contract_sections_id: String(contract_sections_id),
  });

  const payload = {};
  if (completion_percentage_after_correction !== undefined) {
    payload.completion_percentage_after_correction =
      completion_percentage_after_correction;
  }
  if (completion_sum_after_correction !== undefined) {
    payload.completion_sum_after_correction = completion_sum_after_correction;
  }
  if (comment !== undefined) {
    payload.comment = comment;
  }

  const response = await fetch(`${getAPIURL()}general_design/correction?${query}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка обновления корректировки Генплан РП: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Создать согласование по Генплан РП (по разделу контракта).
 * POST /general_design/approval?contract_sections_id=&year=&month=
 * Body: number[] — id согласующих.
 */
export const createGeneralDesignApproval = async (
  contractSectionsId,
  year,
  month,
  approverIds,
) => {
  const response = await fetch(
    `${getAPIURL()}general_design/approval?contract_sections_id=${contractSectionsId}&year=${year}&month=${month}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify(Array.isArray(approverIds) ? approverIds.map(Number) : []),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка отправки Генплан РП на согласование: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Получить текущее согласование по Генплан РП для раздела контракта и периода.
 * GET /general_design/approval?year=&month=&contract_sections_id=
 *
 * Возвращает объект согласования или null, если согласования нет (бэк может
 * ответить 404, пустым телом, либо объектом без id).
 */
export const getGeneralDesignApproval = async (year, month, contractSectionsId) => {
  const response = await fetch(
    `${getAPIURL()}general_design/approval?year=${year}&month=${month}&contract_sections_id=${contractSectionsId}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );

  if (response.status === 404) return null;

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки согласования Генплан РП: ${response.status} ${response.statusText}`,
    );
  }

  const text = await response.text();
  if (!text) return null;
  try {
    const data = JSON.parse(text);
    if (!data || typeof data !== "object" || !data.id) return null;
    return data;
  } catch (_err) {
    return null;
  }
};

/**
 * Получить все согласования Генплан РП, где текущий пользователь — согласующий.
 * GET /general_design/approvals-by-approver
 */
export const getGeneralDesignApprovalsByApprover = async () => {
  const requestInit = {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "include",
  };
  let response = await fetch(
    `${getAPIURL()}general_design/approvals-by-approver`,
    requestInit,
  );
  // Временная обратная совместимость со старым snake_case-роутом.
  if (response.status === 404) {
    response = await fetch(
      `${getAPIURL()}general_design/approvals_by_approver`,
      requestInit,
    );
  }

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка загрузки согласований Генплан РП по согласующему: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Обновить статус согласующего по Генплан РП.
 * PATCH /general_design/approver/{approver_id}/{new_status}
 */
export const updateGeneralDesignApproverStatus = async (
  approverId,
  newStatus,
) => {
  const response = await fetch(
    `${getAPIURL()}general_design/approver/${approverId}/${newStatus}`,
    {
      method: "PATCH",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка обновления статуса согласующего Генплан РП: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
};

/**
 * Удалить согласование по Генплан РП (по разделу контракта/периоду).
 * DELETE /general_design/approval?contract_sections_id=&year=&month=
 */
export const deleteGeneralDesignApproval = async (
  contractSectionsId,
  year,
  month,
) => {
  const response = await fetch(
    `${getAPIURL()}general_design/approval?contract_sections_id=${contractSectionsId}&year=${year}&month=${month}`,
    {
      method: "DELETE",
      headers: { Accept: "application/json" },
      credentials: "include",
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    handleError(
      response,
      errorText,
      `Ошибка удаления согласования Генплан РП: ${response.status} ${response.statusText}`,
    );
  }

  return response.text();
};
